// src/ClinicalDataCollector/components/output/TelehealthSummary.jsx
import React, { useContext, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Alert,
  Button,
  Collapse,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useSelector } from 'react-redux';

import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

const TelehealthSummary = () => {
  // 🔹 id de AdvancedMD desde Redux
  const { advancedMdPatientId } = useSelector(
    state => state.patientMapSelect
  );

  // 🔹 Contexto AdvancedMD (Telehealth 2.0)
  const {
    telehealthNotes,
    telehealthLoading,
    telehealthError,
    loadTelehealthNotes,
  } = useContext(AdvancedMdContext);

  // 🔹 mapa de notas abiertas/cerradas (JS plano)
  const [openMap, setOpenMap] = useState({});

  const toggleNote = noteId => {
    setOpenMap(prev => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  // 🔹 Cargar notas cuando tengamos advancedMdPatientId
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadTelehealthNotes(advancedMdPatientId);
  }, [advancedMdPatientId, loadTelehealthNotes]);

  const notes = telehealthNotes || [];
  const totalNotes = notes.length;

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Typography
        variant="h6"
        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
      >
        <EventNoteIcon color="secondary" />
        <Box ml={1}>Clinical Notes – Telehealth 2.0</Box>
      </Typography>

      {!telehealthLoading &&
        !telehealthError &&
        totalNotes > 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            {totalNotes === 1
              ? '1 telehealth note'
              : `${totalNotes} telehealth notes`}
          </Typography>
        )}

      {telehealthLoading && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          Loading Telehealth 2.0 notes from AdvancedMD…
        </Typography>
      )}

      {telehealthError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading Telehealth 2.0 notes:{' '}
          {telehealthError.message || 'Unknown error'}
        </Alert>
      )}

      {!telehealthLoading &&
        notes.length === 0 &&
        !telehealthError && (
          <Typography variant="body2" color="text.secondary">
            No Telehealth 2.0 notes available for this patient.
          </Typography>
        )}

      {notes.map(note => {
        const f = note.fields || {};
        const dateLabel = note.noteDateTime
          ? new Date(note.noteDateTime).toLocaleString()
          : 'N/A';

        // 🔹 mini resumen: razón + dx (primera línea del HPI)
        const reasonSummary = f.HPI_Description
          ? f.HPI_Description.split('\n')[0]
          : '';
        const dxSummary = f.DX_Diagnosis || '';

        const headerLine = [
          reasonSummary ? `Reason: ${reasonSummary}` : null,
          dxSummary ? `Dx: ${dxSummary}` : null,
        ]
          .filter(Boolean)
          .join(' | ');

        const isOpen = !!openMap[note.noteId];

        return (
          <Box
            key={note.noteId}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Header compacto de la nota */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {note.templateName} – {dateLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Entered by: {note.userName} | Signed by: {note.signedByUser}
              {note.reviewedByUser
                ? ` | Reviewed by: ${note.reviewedByUser}`
                : ''}
            </Typography>

            {headerLine && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {headerLine}
              </Typography>
            )}

            {/* Botón ver/ocultar detalle */}
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => toggleNote(note.noteId)}
              >
                {isOpen ? 'Hide details' : 'View details'}
              </Button>
            </Box>

            {/* Detalle dentro de Collapse */}
            <Collapse in={isOpen}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* HPI + Diagnóstico */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    HPI / Reason for Visit
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', mb: 2 }}
                  >
                    {f.HPI_Description || '—'}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Diagnosis
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {f.DX_Diagnosis || '—'}
                  </Typography>
                </Grid>

                {/* Plan, educación y antecedentes breves */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Visit Summary / Plan
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', mb: 2 }}
                  >
                    {f.AP_VisitSummary || '—'}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Counseling – Nutrition
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', mb: 1 }}
                  >
                    {f.ED_NutritionCounsel || '—'}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ mt: 1 }}
                  >
                    Counseling – Physical Activity
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', mb: 2 }}
                  >
                    {f.ED_PhysicalActivityCounsel || '—'}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Allergies / Current Meds
                  </Typography>
                  <Typography variant="body2">
                    <strong>Allergies:</strong> {f.Hx_Allergies || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Current Meds:</strong>{' '}
                    {f.Hx_CurrentMeds || '—'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Disclaimer */}
              {f.Disclaimer && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Disclaimer
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-line', fontSize: 12 }}
                  >
                    {f.Disclaimer}
                  </Typography>
                </Box>
              )}
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
};

export default TelehealthSummary;
